import { motion, AnimatePresence } from 'framer-motion'
import { useState, useEffect } from 'react'
import {
  Sparkles,
  Rocket,
  Check,
  ArrowRight,
  Bot,
  Zap,
  Shield,
  Bell,
  Moon,
  Globe,
  Cpu,
  Award,
  BookOpen,
  Link,
  Lightbulb,
  CheckCircle
} from 'lucide-react'

interface StepProps {
  onNext: () => void
}

// Quiz questions testing knowledge from Steps 1-5
const quizQuestions = [
  {
    id: 1,
    question: 'What is the primary purpose of an AI Twin assistant?',
    options: [
      'To replace human workers entirely',
      'To learn your style and preferences to provide personalized assistance',
      'To store all your personal data for advertising',
      'To automate social media posting only'
    ],
    correctIndex: 1,
    explanation: 'An AI Twin is designed to learn your unique communication style, preferences, and working patterns to provide highly personalized assistance that feels natural and aligned with your needs.'
  },
  {
    id: 2,
    question: 'How does AssistantTwin ensure privacy in its architecture?',
    options: [
      'By sharing data with third parties for verification',
      'Privacy-first design with local processing and encrypted data storage',
      'By not collecting any data at all',
      'By using public cloud servers without encryption'
    ],
    correctIndex: 1,
    explanation: 'AssistantTwin uses a privacy-first architecture that prioritizes local processing where possible and encrypts all stored data. Your personal information and preferences remain secure and under your control.'
  },
  {
    id: 3,
    question: 'What makes personalization in AI assistants effective?',
    options: [
      'Using the same responses for everyone',
      'Learning from user interactions to adapt communication style and preferences',
      'Asking the same questions repeatedly',
      'Ignoring user feedback'
    ],
    correctIndex: 1,
    explanation: 'Effective personalization comes from continuous learning. The AI observes how you communicate, what tasks you perform frequently, and your preferences to provide increasingly relevant and helpful assistance.'
  },
  {
    id: 4,
    question: 'What is the benefit of AI-filtered notifications?',
    options: [
      'Blocking all notifications completely',
      'Intelligently prioritizing important alerts while reducing noise',
      'Sending more notifications than before',
      'Only showing marketing messages'
    ],
    correctIndex: 1,
    explanation: 'AI-filtered notifications analyze the importance and relevance of incoming alerts based on your patterns, ensuring you see critical information while reducing interruption from less important items.'
  },
  {
    id: 5,
    question: 'How can an AI Twin boost productivity?',
    options: [
      'By doing all work without any human involvement',
      'By automating routine tasks and providing proactive suggestions',
      'By making decisions without user input',
      'By working only during business hours'
    ],
    correctIndex: 1,
    explanation: 'AI Twins boost productivity by handling routine tasks automatically (scheduling, reminders, organization) while proactively suggesting optimizations based on your workflow patterns.'
  }
]

// Practice scenarios for applying AI assistant knowledge
const practiceScenarios = [
  {
    id: 'delegation',
    title: 'Task Delegation',
    description: 'You receive 50 emails daily and spend 2 hours sorting through them. Your AI Twin can help.',
    question: 'What is the best way to leverage your AI Twin for email management?',
    options: [
      { text: 'Have the AI delete all emails automatically', feedback: 'Deleting without review could cause you to miss important communications.' },
      { text: 'Train the AI to categorize, prioritize, and draft responses for routine emails', feedback: 'Excellent! This approach leverages AI for the routine work while keeping you in control of important decisions.' },
      { text: 'Continue managing all emails manually', feedback: 'This does not utilize the AI capabilities you have available.' },
      { text: 'Have the AI respond to all emails without your review', feedback: 'Automated responses without review could lead to inappropriate communications.' }
    ],
    correctIndex: 1
  },
  {
    id: 'privacy',
    title: 'Privacy Configuration',
    description: 'You want to use your AI Twin for work tasks but are concerned about sensitive company data.',
    question: 'What is the best approach to configure privacy settings?',
    options: [
      { text: 'Disable all AI features to be completely safe', feedback: 'This prevents you from benefiting from the assistant while not addressing the real concern.' },
      { text: 'Configure data boundaries: enable AI for appropriate tasks, restrict access to sensitive systems', feedback: 'Perfect! Granular privacy controls let you benefit from AI assistance while maintaining appropriate data boundaries.' },
      { text: 'Share all data since AI is always secure', feedback: 'Even with secure systems, appropriate access controls are important for sensitive data.' },
      { text: 'Use the AI only for personal tasks, never work', feedback: 'This unnecessarily limits the productivity benefits in areas where AI assistance would be appropriate.' }
    ],
    correctIndex: 1
  },
  {
    id: 'personalization',
    title: 'Personality Tuning',
    description: 'Your AI Twin communication style feels too formal for casual conversations with friends.',
    question: 'How should you adjust the AI personality settings?',
    options: [
      { text: 'Accept the formal style for all communications', feedback: 'A one-size-fits-all approach does not leverage the personalization capabilities available.' },
      { text: 'Configure context-aware personality: formal for work, friendly for personal', feedback: 'Excellent! Context-aware settings allow the AI to adapt its communication style based on the audience and situation.' },
      { text: 'Make all communications informal', feedback: 'This could be inappropriate for professional contexts.' },
      { text: 'Disable AI assistance for personal messages', feedback: 'This limits the benefits when the AI could provide helpful personalized assistance.' }
    ],
    correctIndex: 1
  }
]

// Resource library for further exploration
const resourceLibrary = [
  {
    id: 'productivity',
    title: 'Productivity Enhancement',
    icon: Zap,
    topics: [
      'Task automation strategies',
      'Time-saving workflows',
      'Priority management',
      'Focus optimization',
      'Meeting preparation automation'
    ]
  },
  {
    id: 'personalization',
    title: 'Personalization Features',
    icon: Bot,
    topics: [
      'Communication style training',
      'Preference learning',
      'Context awareness',
      'Response customization',
      'Workflow adaptation'
    ]
  },
  {
    id: 'privacy',
    title: 'Privacy & Security',
    icon: Shield,
    topics: [
      'Data encryption methods',
      'Access control settings',
      'Local processing options',
      'Data retention policies',
      'Security best practices'
    ]
  },
  {
    id: 'integration',
    title: 'Integration Capabilities',
    icon: Globe,
    topics: [
      'Calendar synchronization',
      'Email integration',
      'Cloud storage connections',
      'Third-party app APIs',
      'Cross-platform support'
    ]
  },
  {
    id: 'advanced',
    title: 'Advanced Features',
    icon: Cpu,
    topics: [
      'Proactive suggestions',
      'Pattern recognition',
      'Predictive assistance',
      'Multi-modal interactions',
      'Continuous learning'
    ]
  }
]

// Related topics from the network
const relatedTopics = [
  { name: 'Mentor Twin', domain: 'mentortwin.com', description: 'Personalized coaching and mentorship AI' },
  { name: 'Prime AI Compute', domain: 'primeaicompute.com', description: 'AI infrastructure and computing' },
  { name: 'Globally Honest', domain: 'globallyhonest.com', description: 'Ethical AI and transparency' },
  { name: 'Global Knowledge Graph', domain: 'globalknowledgegraph.com', description: 'Explore the full knowledge network' }
]

// Knowledge map concepts for card-based display
const knowledgeMapConcepts = [
  { id: 'aitwin', label: 'AI Twin', group: 'center', description: 'Your personalized AI assistant that learns your unique style, preferences, and workflows to provide tailored assistance.' },
  { id: 'personalization', label: 'Personalization', group: 'core', description: 'The AI learns from your interactions to adapt its communication style, responses, and suggestions to match your preferences.' },
  { id: 'privacy', label: 'Privacy', group: 'core', description: 'Privacy-first architecture ensures your data stays secure with local processing and encrypted storage.' },
  { id: 'productivity', label: 'Productivity', group: 'core', description: 'Automate routine tasks, get proactive suggestions, and optimize your workflow to accomplish more.' },
  { id: 'automation', label: 'Automation', group: 'core', description: 'Set up intelligent automations for repetitive tasks like email sorting, scheduling, and reminders.' },
  { id: 'email', label: 'Email', group: 'secondary', description: 'Smart email management with categorization, priority sorting, and draft suggestions.' },
  { id: 'calendar', label: 'Calendar', group: 'secondary', description: 'Intelligent scheduling, meeting preparation, and time optimization.' },
  { id: 'research', label: 'Research', group: 'secondary', description: 'AI-assisted research and information gathering tailored to your needs.' },
  { id: 'communication', label: 'Communication', group: 'secondary', description: 'Enhanced communication with context-aware messaging and style adaptation.' }
]

const colorScale: { [key: string]: string } = {
  center: 'from-primary-500 to-accent-500',
  core: 'from-blue-500 to-blue-600',
  secondary: 'from-sky-400 to-blue-500'
}

interface ToggleOption {
  id: string
  icon: typeof Bell
  label: string
  description: string
}

const toggleOptions: ToggleOption[] = [
  { id: 'notifications', icon: Bell, label: 'Smart Notifications', description: 'AI-filtered alerts' },
  { id: 'darkMode', icon: Moon, label: 'Dark Mode', description: 'Easy on the eyes' },
  { id: 'autoSync', icon: Globe, label: 'Auto-Sync', description: 'Always up to date' },
  { id: 'aiSuggestions', icon: Cpu, label: 'AI Suggestions', description: 'Proactive help' }
]

const Step6 = ({ onNext }: StepProps) => {
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null)
  const [toggleStates, setToggleStates] = useState<Record<string, boolean>>({
    notifications: true,
    darkMode: false,
    autoSync: true,
    aiSuggestions: true
  })
  const [personalityLevel, setPersonalityLevel] = useState(3)
  const [submitted, setSubmitted] = useState(false)

  // Quiz state
  const [quizStarted, setQuizStarted] = useState(false)
  const [currentQuestion, setCurrentQuestion] = useState(0)
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null)
  const [showExplanation, setShowExplanation] = useState(false)
  const [quizScore, setQuizScore] = useState(0)
  const [quizComplete, setQuizComplete] = useState(false)

  // Practice scenarios state
  const [activeScenario, setActiveScenario] = useState<string | null>(null)
  const [scenarioAnswer, setScenarioAnswer] = useState<number | null>(null)
  const [completedScenarios, setCompletedScenarios] = useState<string[]>([])

  // Resource library state
  const [expandedResources, setExpandedResources] = useState<string[]>([])

  // Achievement state
  const [showAchievement, setShowAchievement] = useState(false)

  // Knowledge map state
  const [selectedConcept, setSelectedConcept] = useState<string | null>(null)

  const handleToggle = (id: string) => {
    setToggleStates(prev => ({ ...prev, [id]: !prev[id] }))
  }

  const handleSubmit = () => {
    if (selectedPlan) {
      setSubmitted(true)
    }
  }

  const benefits = [
    { icon: Bot, text: 'Personal AI assistant that learns your style' },
    { icon: Zap, text: 'Boost productivity by 10x' },
    { icon: Shield, text: 'Privacy-first architecture' }
  ]

  const plans = [
    {
      id: 'starter',
      name: 'Starter',
      price: 'Free',
      features: ['Basic AI assistance', '10 queries/day', 'Email support'],
      popular: false
    },
    {
      id: 'pro',
      name: 'Pro',
      price: '$19',
      period: '/month',
      features: ['Unlimited queries', 'Advanced personalization', 'Priority support', 'API access'],
      popular: true
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: 'Custom',
      features: ['Custom deployment', 'Dedicated support', 'SLA guarantee', 'Advanced security'],
      popular: false
    }
  ]

  const personalityLabels = ['Minimal', 'Reserved', 'Balanced', 'Friendly', 'Expressive']

  // Check for achievement
  useEffect(() => {
    if (quizComplete && completedScenarios.length >= 2 && expandedResources.length >= 3) {
      setShowAchievement(true)
    }
  }, [quizComplete, completedScenarios, expandedResources])

  // Quiz handlers
  const handleAnswerSelect = (index: number) => {
    if (showExplanation) return
    setSelectedAnswer(index)
  }

  const handleCheckAnswer = () => {
    if (selectedAnswer === null) return
    setShowExplanation(true)
    if (selectedAnswer === quizQuestions[currentQuestion].correctIndex) {
      setQuizScore(prev => prev + 1)
    }
  }

  const handleNextQuestion = () => {
    if (currentQuestion < quizQuestions.length - 1) {
      setCurrentQuestion(prev => prev + 1)
      setSelectedAnswer(null)
      setShowExplanation(false)
    } else {
      setQuizComplete(true)
    }
  }

  // Scenario handlers
  const handleScenarioAnswer = (index: number) => {
    setScenarioAnswer(index)
  }

  const completeScenario = () => {
    if (activeScenario && !completedScenarios.includes(activeScenario)) {
      setCompletedScenarios(prev => [...prev, activeScenario])
    }
    setActiveScenario(null)
    setScenarioAnswer(null)
  }

  // Resource handlers
  const toggleResource = (id: string) => {
    if (expandedResources.includes(id)) {
      setExpandedResources(prev => prev.filter(r => r !== id))
    } else {
      setExpandedResources(prev => [...prev, id])
    }
  }

  // Share achievement
  const shareAchievement = () => {
    const text = `I completed the AssistantTwin mastery course! Score: ${quizScore}/${quizQuestions.length} on the quiz, ${completedScenarios.length} scenarios completed. Discover AI assistants at assistanttwin.com`
    navigator.clipboard.writeText(text)
    alert('Achievement copied to clipboard!')
  }

  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center px-4">
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', duration: 0.6 }}
        className="w-20 h-20 rounded-2xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center mb-6 shadow-xl shadow-primary-500/30"
      >
        <Sparkles className="w-10 h-10 text-white" />
      </motion.div>

      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="text-3xl md:text-4xl font-bold text-center mb-4"
      >
        Begin Your <span className="gradient-text">AI Journey</span>
      </motion.h2>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="text-lg text-surface-600 dark:text-surface-400 text-center max-w-2xl mb-8"
      >
        Configure your AI Twin with just a few taps
      </motion.p>

      {/* Benefits */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="flex flex-wrap justify-center gap-6 mb-10"
      >
        {benefits.map((benefit, index) => (
          <motion.div
            key={benefit.text}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5 + index * 0.1 }}
            className="flex items-center gap-3"
          >
            <div className="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center">
              <benefit.icon className="w-5 h-5 text-primary-500" />
            </div>
            <span className="text-surface-700 dark:text-surface-300 font-medium">
              {benefit.text}
            </span>
          </motion.div>
        ))}
      </motion.div>

      {!submitted ? (
        <>
          {/* Pricing Cards - Click to Select */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl mb-10 w-full"
          >
            {plans.map((plan, index) => (
              <motion.button
                key={plan.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 + index * 0.1 }}
                onClick={() => setSelectedPlan(plan.id)}
                className={`card p-6 relative text-left transition-all duration-300 ${
                  selectedPlan === plan.id
                    ? 'ring-2 ring-primary-500 shadow-xl shadow-primary-500/20 scale-[1.02]'
                    : plan.popular
                    ? 'ring-1 ring-primary-300 dark:ring-primary-700'
                    : 'hover:shadow-lg hover:border-primary-300 dark:hover:border-primary-700'
                }`}
              >
                {selectedPlan === plan.id && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-primary-500 flex items-center justify-center shadow-lg"
                  >
                    <Check className="w-4 h-4 text-white" />
                  </motion.div>
                )}
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-primary-500 to-accent-500 text-white text-xs font-medium rounded-full">
                    Most Popular
                  </div>
                )}
                <h3 className="text-xl font-semibold text-surface-900 dark:text-surface-100 mb-2 mt-2">
                  {plan.name}
                </h3>
                <div className="flex items-baseline mb-4">
                  <span className="text-3xl font-bold gradient-text">{plan.price}</span>
                  {plan.period && (
                    <span className="text-surface-500 dark:text-surface-400 ml-1 text-sm">
                      {plan.period}
                    </span>
                  )}
                </div>
                <ul className="space-y-2">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-2 text-sm text-surface-600 dark:text-surface-400">
                      <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.button>
            ))}
          </motion.div>

          {/* Personality Slider */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
            className="w-full max-w-xl mb-8"
          >
            <div className="card p-6">
              <h3 className="text-lg font-semibold text-surface-900 dark:text-surface-100 mb-2 text-center">
                AI Personality Style
              </h3>
              <p className="text-sm text-surface-500 dark:text-surface-400 text-center mb-6">
                How should your AI Twin communicate?
              </p>

              {/* Visual Slider Buttons */}
              <div className="flex justify-between gap-2 mb-3">
                {personalityLabels.map((label, index) => (
                  <button
                    key={label}
                    onClick={() => setPersonalityLevel(index)}
                    className={`flex-1 py-3 px-2 rounded-lg text-xs font-medium transition-all duration-300 ${
                      personalityLevel === index
                        ? 'bg-gradient-to-r from-primary-500 to-accent-500 text-white shadow-lg shadow-primary-500/30'
                        : 'bg-surface-100 dark:bg-surface-700 text-surface-600 dark:text-surface-400 hover:bg-surface-200 dark:hover:bg-surface-600'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {/* Progress bar visualization */}
              <div className="h-2 bg-surface-200 dark:bg-surface-700 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-primary-500 to-accent-500"
                  initial={{ width: '60%' }}
                  animate={{ width: `${((personalityLevel + 1) / 5) * 100}%` }}
                  transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                />
              </div>
            </div>
          </motion.div>

          {/* Toggle Switches */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0 }}
            className="w-full max-w-xl mb-8"
          >
            <div className="card p-6">
              <h3 className="text-lg font-semibold text-surface-900 dark:text-surface-100 mb-4 text-center">
                Feature Preferences
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {toggleOptions.map((option) => (
                  <button
                    key={option.id}
                    onClick={() => handleToggle(option.id)}
                    className={`p-4 rounded-xl border-2 transition-all duration-300 text-left ${
                      toggleStates[option.id]
                        ? 'border-primary-500 bg-primary-500/10'
                        : 'border-surface-200 dark:border-surface-700 bg-white dark:bg-surface-800'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-300 ${
                        toggleStates[option.id]
                          ? 'bg-primary-500 text-white'
                          : 'bg-surface-100 dark:bg-surface-700 text-surface-500'
                      }`}>
                        <option.icon className="w-4 h-4" />
                      </div>
                      {/* Toggle indicator */}
                      <div className={`w-10 h-6 rounded-full p-1 transition-colors duration-300 ${
                        toggleStates[option.id] ? 'bg-primary-500' : 'bg-surface-300 dark:bg-surface-600'
                      }`}>
                        <motion.div
                          className="w-4 h-4 rounded-full bg-white shadow"
                          animate={{ x: toggleStates[option.id] ? 16 : 0 }}
                          transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                        />
                      </div>
                    </div>
                    <h4 className="font-medium text-surface-900 dark:text-surface-100 text-sm">
                      {option.label}
                    </h4>
                    <p className="text-xs text-surface-500 dark:text-surface-400">
                      {option.description}
                    </p>
                  </button>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Submit Button */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1 }}
            className="text-center"
          >
            <button
              onClick={handleSubmit}
              disabled={!selectedPlan}
              className={`btn-primary text-lg group ${!selectedPlan ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <Rocket className="w-5 h-5" />
              {selectedPlan ? 'Start Your Journey' : 'Select a Plan Above'}
              {selectedPlan && <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />}
            </button>
            {!selectedPlan && (
              <p className="text-sm text-surface-500 dark:text-surface-400 mt-3">
                Tap a plan card to continue
              </p>
            )}
          </motion.div>
        </>
      ) : (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="card p-8 text-center max-w-md"
        >
          <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-green-500" />
          </div>
          <h3 className="text-2xl font-bold text-surface-900 dark:text-surface-100 mb-3">
            You're All Set!
          </h3>
          <p className="text-surface-600 dark:text-surface-400 mb-6">
            Your AI Twin is configured with {plans.find(p => p.id === selectedPlan)?.name} plan
            and {personalityLabels[personalityLevel].toLowerCase()} communication style.
          </p>
          <div className="flex flex-wrap justify-center gap-2 mb-6">
            {toggleOptions.filter(opt => toggleStates[opt.id]).map(opt => (
              <span
                key={opt.id}
                className="px-3 py-1 rounded-full bg-primary-500/10 text-primary-600 dark:text-primary-400 text-sm font-medium"
              >
                {opt.label}
              </span>
            ))}
          </div>
          <p className="text-sm text-surface-500 dark:text-surface-400">
            We'll notify you when AssistantTwin is ready for you.
          </p>
        </motion.div>
      )}

      {/* Interactive Summary Quiz */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.2 }}
        className="w-full max-w-4xl mt-16"
      >
        <h3 className="text-xl font-semibold text-surface-900 dark:text-surface-100 mb-6 flex items-center gap-2 justify-center">
          <CheckCircle className="w-6 h-6 text-primary-500" />
          Knowledge Check
        </h3>

        {!quizStarted ? (
          <div className="card p-8 text-center bg-gradient-to-br from-primary-50 to-accent-50 dark:from-primary-900/20 dark:to-accent-900/20">
            <p className="text-surface-700 dark:text-surface-300 mb-4">
              Test your understanding of AI assistants with 5 questions covering personalization, privacy, and productivity features.
            </p>
            <button
              onClick={() => setQuizStarted(true)}
              className="btn-primary"
            >
              Start Quiz
            </button>
          </div>
        ) : !quizComplete ? (
          <div className="card p-6">
            <div className="flex justify-between items-center mb-4">
              <span className="text-sm text-surface-500 dark:text-surface-400">
                Question {currentQuestion + 1} of {quizQuestions.length}
              </span>
              <span className="text-sm font-medium text-primary-600 dark:text-primary-400">
                Score: {quizScore}/{currentQuestion + (showExplanation ? 1 : 0)}
              </span>
            </div>

            <h4 className="text-lg font-semibold text-surface-900 dark:text-surface-100 mb-4">
              {quizQuestions[currentQuestion].question}
            </h4>

            <div className="space-y-3 mb-6">
              {quizQuestions[currentQuestion].options.map((option, index) => (
                <button
                  key={index}
                  onClick={() => handleAnswerSelect(index)}
                  disabled={showExplanation}
                  className={`w-full p-4 rounded-xl text-left transition-all duration-300 ${
                    showExplanation
                      ? index === quizQuestions[currentQuestion].correctIndex
                        ? 'bg-green-100 dark:bg-green-900/30 border-2 border-green-500'
                        : selectedAnswer === index
                        ? 'bg-red-100 dark:bg-red-900/30 border-2 border-red-500'
                        : 'bg-surface-50 dark:bg-surface-700/50 border border-surface-200 dark:border-surface-600'
                      : selectedAnswer === index
                      ? 'bg-primary-100 dark:bg-primary-900/30 border-2 border-primary-500'
                      : 'bg-surface-50 dark:bg-surface-700/50 border border-surface-200 dark:border-surface-600 hover:border-primary-300'
                  }`}
                >
                  <span className="text-surface-700 dark:text-surface-300">{option}</span>
                </button>
              ))}
            </div>

            <AnimatePresence>
              {showExplanation && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mb-4 p-4 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800"
                >
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    {quizQuestions[currentQuestion].explanation}
                  </p>
                </motion.div>
              )}
            </AnimatePresence>

            {!showExplanation ? (
              <button
                onClick={handleCheckAnswer}
                disabled={selectedAnswer === null}
                className={`w-full py-3 rounded-xl font-semibold transition-all duration-300 ${
                  selectedAnswer === null
                    ? 'bg-surface-200 dark:bg-surface-700 text-surface-400 cursor-not-allowed'
                    : 'btn-primary'
                }`}
              >
                Check Answer
              </button>
            ) : (
              <button
                onClick={handleNextQuestion}
                className="w-full btn-primary"
              >
                {currentQuestion < quizQuestions.length - 1 ? 'Next Question' : 'See Results'}
              </button>
            )}
          </div>
        ) : (
          <div className="card p-8 text-center bg-gradient-to-br from-primary-50 to-accent-50 dark:from-primary-900/20 dark:to-accent-900/20">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
              <span className="text-3xl font-bold text-white">{quizScore}/{quizQuestions.length}</span>
            </div>
            <h4 className="text-xl font-bold text-surface-900 dark:text-surface-100 mb-2">
              {quizScore >= 4 ? 'Excellent!' : quizScore >= 3 ? 'Good Job!' : 'Keep Learning!'}
            </h4>
            <p className="text-surface-600 dark:text-surface-400 mb-4">
              You scored {quizScore} out of {quizQuestions.length} questions correctly.
            </p>
            <button
              onClick={() => {
                setQuizStarted(false)
                setCurrentQuestion(0)
                setSelectedAnswer(null)
                setShowExplanation(false)
                setQuizScore(0)
                setQuizComplete(false)
              }}
              className="btn-secondary"
            >
              Retake Quiz
            </button>
          </div>
        )}
      </motion.div>

      {/* Knowledge Map - Card-based */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.3 }}
        className="w-full max-w-4xl mt-10"
      >
        <h3 className="text-xl font-semibold text-surface-900 dark:text-surface-100 mb-6 flex items-center gap-2 justify-center">
          <Link className="w-6 h-6 text-primary-500" />
          Knowledge Map
        </h3>
        <div className="card p-6">
          <p className="text-sm text-surface-500 dark:text-surface-400 mb-6 text-center">
            Click on concepts to explore AI Twin capabilities and integrations
          </p>

          {/* Concept Cards Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
            {knowledgeMapConcepts.map((concept) => (
              <motion.button
                key={concept.id}
                onClick={() => setSelectedConcept(selectedConcept === concept.id ? null : concept.id)}
                whileHover={{ scale: 1.03, y: -2 }}
                whileTap={{ scale: 0.98 }}
                className={`p-4 rounded-xl bg-gradient-to-br ${colorScale[concept.group]} text-white text-center transition-all duration-300 ${
                  selectedConcept === concept.id ? 'ring-2 ring-white shadow-xl' : 'hover:shadow-lg'
                }`}
              >
                <p className="text-sm font-medium">{concept.label}</p>
              </motion.button>
            ))}
          </div>

          {/* Selected Concept Detail */}
          <AnimatePresence>
            {selectedConcept && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="p-4 bg-primary-100 dark:bg-primary-900/30 rounded-xl border border-primary-200 dark:border-primary-800"
              >
                <h4 className="font-semibold text-surface-900 dark:text-surface-100 mb-2">
                  {knowledgeMapConcepts.find(c => c.id === selectedConcept)?.label}
                </h4>
                <p className="text-sm text-surface-600 dark:text-surface-400">
                  {knowledgeMapConcepts.find(c => c.id === selectedConcept)?.description}
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>

      {/* Practice Scenarios */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.4 }}
        className="w-full max-w-4xl mt-10"
      >
        <h3 className="text-xl font-semibold text-surface-900 dark:text-surface-100 mb-6 flex items-center gap-2 justify-center">
          <Lightbulb className="w-6 h-6 text-primary-500" />
          Practice Scenarios
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {practiceScenarios.map((scenario) => (
            <motion.button
              key={scenario.id}
              onClick={() => setActiveScenario(scenario.id)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`card p-4 text-left transition-all duration-300 ${
                completedScenarios.includes(scenario.id)
                  ? 'bg-green-50 dark:bg-green-900/20 ring-2 ring-green-300 dark:ring-green-700'
                  : 'hover:ring-2 hover:ring-primary-300 dark:hover:ring-primary-600'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-surface-900 dark:text-surface-100">
                  {scenario.title}
                </h4>
                {completedScenarios.includes(scenario.id) && (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                )}
              </div>
              <p className="text-sm text-surface-500 dark:text-surface-400 line-clamp-2">
                {scenario.description}
              </p>
            </motion.button>
          ))}
        </div>

        {/* Scenario Modal */}
        <AnimatePresence>
          {activeScenario && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
              onClick={() => {
                setActiveScenario(null)
                setScenarioAnswer(null)
              }}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white dark:bg-surface-800 rounded-2xl p-6 max-w-lg w-full max-h-[80vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                {(() => {
                  const scenario = practiceScenarios.find(s => s.id === activeScenario)!
                  return (
                    <>
                      <h4 className="text-xl font-bold text-surface-900 dark:text-surface-100 mb-3">
                        {scenario.title}
                      </h4>
                      <p className="text-surface-600 dark:text-surface-400 mb-4">
                        {scenario.description}
                      </p>
                      <p className="font-medium text-surface-800 dark:text-surface-200 mb-4">
                        {scenario.question}
                      </p>

                      <div className="space-y-3 mb-6">
                        {scenario.options.map((option, index) => (
                          <button
                            key={index}
                            onClick={() => handleScenarioAnswer(index)}
                            className={`w-full p-4 rounded-xl text-left transition-all duration-300 ${
                              scenarioAnswer === index
                                ? index === scenario.correctIndex
                                  ? 'bg-green-100 dark:bg-green-900/30 border-2 border-green-500'
                                  : 'bg-amber-100 dark:bg-amber-900/30 border-2 border-amber-500'
                                : 'bg-surface-50 dark:bg-surface-700/50 border border-surface-200 dark:border-surface-600 hover:border-primary-300'
                            }`}
                          >
                            <span className="text-surface-700 dark:text-surface-300">{option.text}</span>
                            {scenarioAnswer === index && (
                              <p className="mt-2 text-sm text-surface-500 dark:text-surface-400">
                                {option.feedback}
                              </p>
                            )}
                          </button>
                        ))}
                      </div>

                      {scenarioAnswer !== null && (
                        <button
                          onClick={completeScenario}
                          className="w-full btn-primary"
                        >
                          Continue
                        </button>
                      )}
                    </>
                  )
                })()}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Resource Library */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.5 }}
        className="w-full max-w-4xl mt-10"
      >
        <h3 className="text-xl font-semibold text-surface-900 dark:text-surface-100 mb-6 flex items-center gap-2 justify-center">
          <BookOpen className="w-6 h-6 text-primary-500" />
          Resource Library
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {resourceLibrary.map((resource) => {
            const Icon = resource.icon
            return (
              <div
                key={resource.id}
                className="card overflow-hidden"
              >
                <button
                  onClick={() => toggleResource(resource.id)}
                  className="w-full p-4 flex items-center gap-3 hover:bg-surface-50 dark:hover:bg-surface-700/50 transition-all duration-300"
                >
                  <div className="w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 flex items-center justify-center">
                    <Icon className="w-5 h-5" />
                  </div>
                  <span className="flex-1 text-left font-medium text-surface-900 dark:text-surface-100">
                    {resource.title}
                  </span>
                  <svg
                    className={`w-5 h-5 text-surface-400 transition-transform duration-300 ${
                      expandedResources.includes(resource.id) ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                <AnimatePresence>
                  {expandedResources.includes(resource.id) && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="border-t border-surface-200 dark:border-surface-700"
                    >
                      <ul className="p-4 space-y-2">
                        {resource.topics.map((topic, index) => (
                          <li
                            key={index}
                            className="flex items-center gap-2 text-sm text-surface-600 dark:text-surface-400"
                          >
                            <ArrowRight className="w-4 h-4 text-primary-500 flex-shrink-0" />
                            {topic}
                          </li>
                        ))}
                      </ul>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )
          })}
        </div>
      </motion.div>

      {/* Achievement Badge */}
      <AnimatePresence>
        {showAchievement && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="w-full max-w-4xl mt-10"
          >
            <div className="card p-8 text-center bg-gradient-to-br from-yellow-400 via-amber-500 to-orange-500 text-white">
              <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-white/20 flex items-center justify-center">
                <Award className="w-12 h-12" />
              </div>
              <h3 className="text-2xl font-bold mb-2">AI Twin Expert</h3>
              <p className="text-white/80 mb-4">
                Congratulations! You have mastered the AssistantTwin platform.
              </p>
              <div className="flex justify-center gap-6 mb-6 text-sm">
                <div>
                  <div className="text-2xl font-bold">{quizScore}/{quizQuestions.length}</div>
                  <div className="text-white/70">Quiz Score</div>
                </div>
                <div>
                  <div className="text-2xl font-bold">{completedScenarios.length}</div>
                  <div className="text-white/70">Scenarios</div>
                </div>
                <div>
                  <div className="text-2xl font-bold">{expandedResources.length}</div>
                  <div className="text-white/70">Resources</div>
                </div>
              </div>
              <button
                onClick={shareAchievement}
                className="px-6 py-3 bg-white/20 hover:bg-white/30 rounded-xl font-semibold transition-all duration-300"
              >
                Share Achievement
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Continue Learning */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.6 }}
        className="w-full max-w-4xl mt-10"
      >
        <h3 className="text-xl font-semibold text-surface-900 dark:text-surface-100 mb-6 flex items-center gap-2 justify-center">
          <Globe className="w-6 h-6 text-primary-500" />
          Continue Learning
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {relatedTopics.map((topic) => (
            <a
              key={topic.domain}
              href={`https://${topic.domain}`}
              target="_blank"
              rel="noopener noreferrer"
              className="card p-4 hover:ring-2 hover:ring-primary-300 dark:hover:ring-primary-600 transition-all duration-300 group"
            >
              <h4 className="font-semibold text-surface-900 dark:text-surface-100 group-hover:text-primary-600 dark:group-hover:text-primary-400 mb-1 transition-colors duration-300">
                {topic.name}
              </h4>
              <p className="text-sm text-surface-500 dark:text-surface-400 mb-2">
                {topic.description}
              </p>
              <span className="text-xs text-primary-600 dark:text-primary-400">
                {topic.domain} →
              </span>
            </a>
          ))}
        </div>
      </motion.div>

      {/* CTA */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.7 }}
        className="mt-10 text-center"
      >
        <button onClick={onNext} className="btn-ghost text-lg group">
          Restart Demo
          <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
        </button>
      </motion.div>
    </div>
  )
}

export default Step6
